import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { FaqGroup, IFaqGroup } from '../models/faq-group.model';
import { HttpClient } from '@angular/common/http';
import { SpinnerService } from './spinner.service';
import { ToastService } from './toast.service';
import { environment } from '../../environments/environment';
import { IServerResponse, ServerResponse } from '../models/server-response.model';
import { Platform } from '../models/platform.model';

/* Cuerpo exacto de FaqGroupOrderRequest: SIEMPRE la lista completa de ids en
   el orden deseado — la posición dentro del arreglo es el sortOrder. Si llega
   incompleta, con ids repetidos o con ids de otra plataforma el backend
   responde 400. */
export interface IFaqGroupOrderRequest {
  faqGroupIds: string[];
}

/* Cuerpo exacto de FaqGroupRequest: los dos campos son obligatorios, no hay
   PATCH parcial. */
export interface IFaqGroupRequest {
  title: string;
  status: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class FaqGroupService {

  public loading: boolean = false;
  public faqGroups: FaqGroup[] = [];

  constructor(
    public authService: AuthService,
    private http: HttpClient,
    public spinnerService: SpinnerService,
    public toastService: ToastService
  ) { }

  /*
   ██████╗██████╗ ███████╗ █████╗ ████████╗███████╗
  ██╔════╝██╔══██╗██╔════╝██╔══██╗╚══██╔══╝██╔════╝
  ██║     ██████╔╝█████╗  ███████║   ██║   █████╗
  ██║     ██╔══██╗██╔══╝  ██╔══██║   ██║   ██╔══╝
  ╚██████╗██║  ██║███████╗██║  ██║   ██║   ███████╗
   ╚═════╝╚═╝  ╚═╝╚══════╝╚═╝  ╚═╝   ╚═╝   ╚══════╝

  */
  async create(
    platform: Platform,
    faqGroup: IFaqGroupRequest
  ): Promise<boolean> {

    let flag = false;

    const urlApi = `${environment.urlApi}/platforms/${platform.platformId}/faq-groups`;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Registrando grupo de preguntas frecuentes';

    try {
      const token = await this.authService.getToken();
      const response = await new Promise<ServerResponse>(
        (resolve, reject) => {
          this.http.post<IServerResponse>(
            urlApi,
            JSON.stringify(faqGroup),
            { headers: { "content-type": "application/json", 'Authorization': token.toString() } }
          ).subscribe({
            next: (response: IServerResponse) => resolve(new ServerResponse(response)),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
                this.toastService.show({ header: 'Error de servidor', body: `${error}`, classname: 'bg-danger text-white' });
              }
            },
          });
        }
      );

      if(response.statusCode === 201) {
        flag = true;
        this.toastService.show({ body: `${response.message}`, classname: 'bg-success text-white' });
      } else {
        this.toastService.show({ header: 'Error de servidor', body: `${response.message}`, classname: 'bg-danger text-white' });
      }

    } catch(error) {
      console.log(error);
      this.toastService.show({ header: 'Error', body: `${error}`, classname: 'bg-danger text-white' });
    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';

    return flag;
  }

  /*
  ██████╗ ███████╗██╗     ███████╗████████╗███████╗
  ██╔══██╗██╔════╝██║     ██╔════╝╚══██╔══╝██╔════╝
  ██║  ██║█████╗  ██║     █████╗     ██║   █████╗
  ██║  ██║██╔══╝  ██║     ██╔══╝     ██║   ██╔══╝
  ██████╔╝███████╗███████╗███████╗   ██║   ███████╗
  ╚═════╝ ╚══════╝╚══════╝╚══════╝   ╚═╝   ╚══════╝

  */
  async delete(
    platform: Platform,
    faqGroup: FaqGroup
  ): Promise<boolean> {

    let flag = false;

    const urlApi = `${environment.urlApi}/platforms/${platform.platformId}/faq-groups/${faqGroup.faqGroupId}`;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Eliminando grupo de preguntas frecuentes';

    try {
      const token = await this.authService.getToken();
      const response = await new Promise<ServerResponse>(
        (resolve, reject) => {
          this.http.delete<IServerResponse>(
            urlApi,
            { headers: { 'Authorization': token.toString() } }
          ).subscribe({
            next: (response: IServerResponse) => resolve(new ServerResponse(response)),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
                this.toastService.show({ header: 'Error de servidor', body: `${error}`, classname: 'bg-danger text-white' });
              }
            },
          });
        }
      );

      if(response.statusCode === 200) {
        flag = true;
        this.toastService.show({ body: `${response.message}`, classname: 'bg-success text-white' });
      } else {
        this.toastService.show({ header: 'Error de servidor', body: `${response.message}`, classname: 'bg-danger text-white' });
      }

    } catch(error) {
      console.log(error);
      this.toastService.show({ header: 'Error', body: `${error}`, classname: 'bg-danger text-white' });
    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';

    return flag;
  }

  /*
   ██████╗ ███████╗████████╗
  ██╔════╝ ██╔════╝╚══██╔══╝
  ██║  ███╗█████╗     ██║
  ██║   ██║██╔══╝     ██║
  ╚██████╔╝███████╗   ██║
   ╚═════╝ ╚══════╝   ╚═╝

  */
  async get(
    platform: Platform,
    faqGroupId: string
  ): Promise<FaqGroup | null> {

    let faqGroup: FaqGroup | null = null;

    const urlApi = `${environment.urlApi}/platforms/${platform.platformId}/faq-groups/${faqGroupId}`;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Obteniendo grupo de preguntas frecuentes';

    try {
      const token = await this.authService.getToken();
      const response = await new Promise<ServerResponse<{ faqGroup: IFaqGroup }>>(
        (resolve, reject) => {
          this.http.get<IServerResponse<{ faqGroup: IFaqGroup }>>(
            urlApi,
            { headers: { "content-type": "application/json", "Authorization": token.toString() } }
          ).subscribe({
            next: (response: IServerResponse<{ faqGroup: IFaqGroup }>) => resolve(new ServerResponse<{ faqGroup: IFaqGroup }>(response)),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
              }
            },
          });
        }
      );

      if(response.statusCode === 200) {
        faqGroup = new FaqGroup(response.data!.faqGroup);
      } else {
        this.toastService.show({ header: 'Error de servidor', body: `${response.message}`, classname: 'bg-danger text-white' });
      }

    } catch(error) {
      console.log(error);
    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';

    return faqGroup;
  }

  /*
   ██████╗ ███████╗████████╗ █████╗ ██╗     ██╗
  ██╔════╝ ██╔════╝╚══██╔══╝██╔══██╗██║     ██║
  ██║  ███╗█████╗     ██║   ███████║██║     ██║
  ██║   ██║██╔══╝     ██║   ██╔══██║██║     ██║
  ╚██████╔╝███████╗   ██║   ██║  ██║███████╗███████╗
   ╚═════╝ ╚══════╝   ╚═╝   ╚═╝  ╚═╝╚══════╝╚══════╝

  */
  async getAll(
    platform: Platform
  ) {

    this.loading = true;

    const urlApi = `${environment.urlApi}/platforms/${platform.platformId}/faq-groups`;

    try {
      const token = await this.authService.getToken();
      const response = await new Promise<ServerResponse<{ faqGroups: IFaqGroup[] }>>(
        (resolve, reject) => {
          this.http.get<IServerResponse<{ faqGroups: IFaqGroup[] }>>(
            urlApi,
            { headers: { "content-type": "application/json", "Authorization": token.toString() } }
          ).subscribe({
            next: (response: IServerResponse<{ faqGroups: IFaqGroup[] }>) => resolve(new ServerResponse<{ faqGroups: IFaqGroup[] }>(response)),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
              }
            },
          });
        }
      );

      if(response.statusCode === 200) {
        this.faqGroups = response.data!.faqGroups.map(faqGroup => new FaqGroup(faqGroup));
      } else {
        this.toastService.show({ header: 'Error de servidor', body: `${response.message}`, classname: 'bg-danger text-white' });
      }

    } catch(error) {
      console.log(error);
    }

    this.loading = false;
  }

  /*
  ██╗   ██╗██████╗ ██████╗  █████╗ ████████╗███████╗
  ██║   ██║██╔══██╗██╔══██╗██╔══██╗╚══██╔══╝██╔════╝
  ██║   ██║██████╔╝██║  ██║███████║   ██║   █████╗
  ██║   ██║██╔═══╝ ██║  ██║██╔══██║   ██║   ██╔══╝
  ╚██████╔╝██║     ██████╔╝██║  ██║   ██║   ███████╗
   ╚═════╝ ╚═╝     ╚═════╝ ╚═╝  ╚═╝   ╚═╝   ╚══════╝

  */
  async update(
    platform: Platform,
    faqGroupId: string,
    faqGroup: IFaqGroupRequest
  ): Promise<boolean> {

    let flag = false;

    const urlApi = `${environment.urlApi}/platforms/${platform.platformId}/faq-groups/${faqGroupId}`;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Actualizando grupo de preguntas frecuentes';

    try {
      const token = await this.authService.getToken();
      const response = await new Promise<ServerResponse>(
        (resolve, reject) => {
          this.http.put<IServerResponse>(
            urlApi,
            JSON.stringify(faqGroup),
            { headers: { "content-type": "application/json", 'Authorization': token.toString() } }
          ).subscribe({
            next: (response: IServerResponse) => resolve(new ServerResponse(response)),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
                this.toastService.show({ header: 'Error de servidor', body: `${error}`, classname: 'bg-danger text-white' });
              }
            },
          });
        }
      );

      if(response.statusCode === 200) {
        flag = true;
        this.toastService.show({ body: `${response.message}`, classname: 'bg-success text-white' });
      } else {
        this.toastService.show({ header: 'Error de servidor', body: `${response.message}`, classname: 'bg-danger text-white' });
      }

    } catch(error) {
      console.log(error);
      this.toastService.show({ header: 'Error', body: `${error}`, classname: 'bg-danger text-white' });
    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';

    return flag;
  }

  /*
  ██╗   ██╗██████╗ ██████╗  █████╗ ████████╗███████╗ ██████╗ ██████╗ ██████╗ ███████╗██████╗
  ██║   ██║██╔══██╗██╔══██╗██╔══██╗╚══██╔══╝██╔════╝██╔═══██╗██╔══██╗██╔══██╗██╔════╝██╔══██╗
  ██║   ██║██████╔╝██║  ██║███████║   ██║   █████╗  ██║   ██║██████╔╝██║  ██║█████╗  ██████╔╝
  ██║   ██║██╔═══╝ ██║  ██║██╔══██║   ██║   ██╔══╝  ██║   ██║██╔══██╗██║  ██║██╔══╝  ██╔══██╗
  ╚██████╔╝██║     ██████╔╝██║  ██║   ██║   ███████╗╚██████╔╝██║  ██║██████╔╝███████╗██║  ██║
   ╚═════╝ ╚═╝     ╚═════╝ ╚═╝  ╚═╝   ╚═╝   ╚══════╝ ╚═════╝ ╚═╝  ╚═╝╚═════╝ ╚══════╝╚═╝  ╚═╝

  */
  /* A propósito NO levanta el spinnerService: reordenar es una acción de alta
     frecuencia y el overlay global bloqueando toda la pantalla en cada
     movimiento la vuelve inusable. El indicador de guardado va en la pieza
     afectada, dentro de la pantalla.

     Tampoco muestra toast en el camino feliz — el resultado ya se ve en la
     lista. El toast se reserva para el error, que es lo único que el usuario
     no puede deducir mirando. */
  async updateOrder(
    platform: Platform,
    faqGroupIds: string[]
  ): Promise<FaqGroup[] | null> {

    let faqGroups: FaqGroup[] | null = null;

    const urlApi = `${environment.urlApi}/platforms/${platform.platformId}/faq-groups/order`;

    const body: IFaqGroupOrderRequest = { faqGroupIds: faqGroupIds };

    try {
      const token = await this.authService.getToken();
      const response = await new Promise<ServerResponse<{ faqGroups: IFaqGroup[] }>>(
        (resolve, reject) => {
          this.http.put<IServerResponse<{ faqGroups: IFaqGroup[] }>>(
            urlApi,
            JSON.stringify(body),
            { headers: { "content-type": "application/json", 'Authorization': token.toString() } }
          ).subscribe({
            next: (response: IServerResponse<{ faqGroups: IFaqGroup[] }>) => resolve(new ServerResponse<{ faqGroups: IFaqGroup[] }>(response)),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
                this.toastService.show({ header: 'Error de servidor', body: `${error}`, classname: 'bg-danger text-white' });
              }
            },
          });
        }
      );

      if(response.statusCode === 200) {
        /* El endpoint devuelve la colección completa ya reordenada: se regresa
           tal cual para que la pantalla refresque los sortOrder sin volver a
           pedir el árbol. */
        faqGroups = response.data!.faqGroups.map(faqGroup => new FaqGroup(faqGroup));
      } else {
        /* El 400 del backend ya viene redactado en español para el usuario. */
        this.toastService.show({ header: 'No se pudo guardar el orden', body: `${response.message}`, classname: 'bg-danger text-white' });
      }

    } catch(error) {
      console.log(error);
      this.toastService.show({ header: 'Error', body: `${error}`, classname: 'bg-danger text-white' });
    }

    return faqGroups;
  }
}
